import { DateTime } from "luxon";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgFilterAutocomplete } from "@intelligems/ig-theme";
import {
  AnalyticsResourceType,
  AudienceFilter,
  ChannelType,
  CountryEnum,
  DeviceType,
  StatSigViewType,
  UrlParamPayloadType,
  VisitorType,
} from "@intelligems/ig-types";
import {
  ensureNumber,
  getFirstStartedAtTs,
  getLastEndedAtTs,
} from "@intelligems/ig-utils";

import { useConfiguration } from "../../api/configurations/use-configuration";
import { usePatchExperience } from "../../api/experiences/patch-experience";
import {
  getInitialStartFilterTime,
  getLastEndFilterTime,
} from "../../api/formatters/dashboard";
import type { FilterDialogButtons } from "../../api/types/dashboards";
import { DashboardPageName } from "../../api/types/dashboards";
import { setDashboardFilter } from "../../store/slices/dashboardFilters";
import { setDialog } from "../../store/slices/global";
import { IExperienceDashboardFilters } from "../../store/types/dashboardFilters";
import { IAppState } from "../../store/types/store";
import { ExperienceFe } from "../../types/schemas/experiences";
import { Channel } from "../AudienceFilters/Channel";

import { AnalyticsViewTypeFilter } from "./AnalyticsViewTypeFilter";
import { BaseFiltersDialog } from "./BaseFiltersDialog";
import { DateRangePicker } from "./DateRangePicker";
import { DeviceTypeFilter } from "./DeviceTypeFilter";
import { DiscountFilter } from "./DiscountFilter";
import { UrlParamFilter } from "./UrlParamFilter";
import { UserBehaviorFilters } from "./UserBehaviorFilters";
import { VisitorTypeFilter } from "./VisitorTypeFilter";
import { ZScoreFilter } from "./ZScoreFilter";

interface FiltersDialogProps {
  pageName?: DashboardPageName;
  currentResource: ExperienceFe;
  openFilters: boolean;
  resourceType: AnalyticsResourceType;
  allCustomEventData?: string[];
  allCountryCodesData?: string[];
}

export const FiltersDialog = ({
  currentResource,
  pageName,
  openFilters,
  resourceType,
  allCustomEventData,
  allCountryCodesData,
}: FiltersDialogProps) => {
  const dispatch = useDispatch();
  const dashboardFilters = useSelector(
    (state: IAppState) => state.dashboardFilters
  );

  const mutateExperience = usePatchExperience(currentResource.id);
  const shouldShowDiscount = useConfiguration<boolean>(
    "should_show_volume_discounts_dashboard_filter"
  );

  const maxZScoreConfig = useConfiguration("max_z_score");
  const [dialogFilters, setDialogFilters] =
    useImmer<IExperienceDashboardFilters>({
      ...dashboardFilters,
      statSigView: dashboardFilters.statSigView ?? "best",
      testOrdersOnly:
        dashboardFilters.testOrdersOnly !== undefined
          ? dashboardFilters.testOrdersOnly
          : currentResource.experienceAnalysis.analyticsViewType === "none" ||
            currentResource.experienceAnalysis.analyticsViewType ===
              "test_orders_only",
      discountIncludeType: dashboardFilters.discountIncludeType || 1,
      isZScoreEnabled: dashboardFilters.isZScoreEnabled || true,
      maxZScore: maxZScoreConfig!.value,
      customEvents: dashboardFilters.customEvents || [],
      countryCodes: dashboardFilters.countryCodes || [],
      userBehavior: dashboardFilters.userBehavior ?? {
        viewedCheckout: null,
        addedToCart: null,
      },
      startTime: getInitialStartFilterTime(currentResource, dashboardFilters),
      endTime: getLastEndFilterTime(currentResource, dashboardFilters),
      deviceType: dashboardFilters.deviceType || "any",
      visitorType: dashboardFilters.visitorType || "any",
      channels: dashboardFilters.channels || [],
      urlParam: {
        queryParam:
          dashboardFilters.urlParam?.queryParam !== null
            ? dashboardFilters.urlParam?.queryParam
            : null,
        queryParamValue:
          dashboardFilters.urlParam?.queryParamValue !== null
            ? dashboardFilters.urlParam?.queryParamValue
            : null,
        queryParamFilterType:
          dashboardFilters.urlParam?.queryParamFilterType !== null
            ? dashboardFilters.urlParam?.queryParamFilterType
            : null,
      },
    });

  const [isTestProductsDefaultChange, setIsTestProductsDefaultChange] =
    useState<boolean>(false);
  const [isStartValid, setIsStartValid] = useImmer<boolean>(true);
  const [isEndValid, setIsEndValid] = useImmer<boolean>(true);
  const testType =
    resourceType === "campaigns" ? "campaign" : currentResource.type;

  const onTimeChange = (
    newValue: DateTime | null,
    filterType: "start" | "end"
  ) => {
    if (filterType === "start") {
      setIsStartValid(newValue === null ? false : newValue.isValid);
      setDialogFilters((draft) => {
        draft.startTime = newValue?.toMillis() ?? null;
        return draft;
      });
    } else {
      setIsEndValid(newValue === null ? true : newValue.isValid);
      setDialogFilters((draft) => {
        draft.endTime = newValue?.toMillis() ?? null;
        return draft;
      });
    }
  };

  const onStringFilterChange = (
    newValue: DeviceType | VisitorType | ChannelType | StatSigViewType,
    filterKey: "statSigView" | "deviceType" | "channelType" | "visitorType"
  ) => {
    setDialogFilters((draft) => {
      if (filterKey === "statSigView") {
        draft[filterKey] = newValue as StatSigViewType;
      } else if (filterKey === "deviceType") {
        draft[filterKey] = newValue as DeviceType;
      } else if (filterKey === "channelType") {
        draft[filterKey] = newValue as ChannelType;
      } else if (filterKey === "visitorType") {
        draft[filterKey] = newValue as VisitorType;
      }
      return draft;
    });
  };

  const onBooleanFilterChange = (
    checked: boolean,
    filterKey: "isTestOrdersOnly" | "isZScoreEnabled"
  ) => {
    setDialogFilters((draft) => {
      draft[filterKey] = checked;
      return draft;
    });
  };

  const onStringArrFilterChange = (
    newValues: string[],
    filterKey: "customEvents" | "countryCodes"
  ) => {
    setDialogFilters((draft) => {
      draft[filterKey] = newValues;
      return draft;
    });
  };

  const onChannelClick = (channelType: ChannelType | null) => {
    const updatedChannels = dialogFilters.channels.filter(
      (channel) => !!channel
    );
    if (!channelType) {
      setDialogFilters((draft) => {
        draft.channels = [];
        return draft;
      });
    } else if (updatedChannels.includes(channelType)) {
      setDialogFilters((draft) => {
        if (draft.channels.length === 1) {
          // always default back to any when nothing else selected
          draft.channels = [];
        } else {
          draft.channels = draft.channels.filter(
            (channel) => channel !== channelType
          );
        }
        return draft;
      });
    } else {
      setDialogFilters((draft) => {
        draft.channels = [...(updatedChannels || []), channelType];
      });
    }
  };

  const onUrlParamChange = (
    value: string | AudienceFilter | undefined,
    key: keyof UrlParamPayloadType
  ) => {
    setDialogFilters((draft) => {
      if (!draft.urlParam) {
        draft.urlParam = {
          queryParam: null,
          queryParamFilterType: null,
          queryParamValue: null,
        };
        // @ts-expect-error
        draft.urlParam[key] = value;
      } else {
        // @ts-expect-error
        draft.urlParam[key] = value;
      }
      return draft;
    });
  };

  const filterButtons: FilterDialogButtons[] = [
    {
      label: "Date Range",
      settings: [
        {
          key: "dateRange",
          description: "Select a start and/or end time",
          component: (
            <DateRangePicker
              startFilter={DateTime.fromMillis(
                ensureNumber(dialogFilters.startTime) ?? 0
              )}
              endFilter={
                dialogFilters.endTime
                  ? DateTime.fromMillis(ensureNumber(dialogFilters.endTime))
                  : null
              }
              onTimeChange={onTimeChange}
              resourceMinTime={
                getFirstStartedAtTs(currentResource.experienceActions) ?? 0
              }
              isEndValid={isEndValid}
              isStartValid={isStartValid}
            />
          ),
        },
      ],
      showOnPage: "all",
    },
    {
      label: "Analytics View Type",
      settings: [
        {
          key: "testProdSwitch",
          description: "Set which orders you want to see in the analytics",
          component: (
            <AnalyticsViewTypeFilter
              currentResource={currentResource}
              isTestOrdersOnly={dialogFilters.testOrdersOnly || false}
              onBooleanFilterChange={onBooleanFilterChange}
              setIsTestProductsDefaultChange={setIsTestProductsDefaultChange}
              isTestProductsDefaultChange={isTestProductsDefaultChange}
              resourceType={resourceType}
            />
          ),
        },
      ],
      config:
        currentResource &&
        resourceType === "experiences" &&
        (testType === "pricing" || testType === "campaign"),
      showOnPage: "all",
      hasDefault: true,
      tooltip:
        "Test Orders Only: Include only orders that contain test products. Sitewide Orders: Include all orders placed by visitors exposed to the test, regardless of whether they contain test products",
    },
    {
      label: "Device Type",
      settings: [
        {
          key: "deviceType",
          description: "Filter by device type",
          component: (
            <DeviceTypeFilter
              onStringFilterChange={onStringFilterChange}
              deviceType={dialogFilters.deviceType}
            />
          ),
        },
      ],
      isSwitch: false,
      showOnPage: "all",
    },
    {
      label: "Visitor Type",
      settings: [
        {
          key: "visitorType",
          description: "Filter by visitor type",
          component: (
            <VisitorTypeFilter
              onStringFilterChange={onStringFilterChange}
              visitorType={dialogFilters.visitorType}
            />
          ),
        },
      ],
      isSwitch: false,
      showOnPage: "all",
    },
    {
      label: "Channels & Traffic Sources",
      settings: [
        {
          key: "channelType",
          description:
            "Filter by one more more channel and traffic source type",
          component: (
            <Channel
              onClick={onChannelClick}
              channels={dialogFilters.channels}
            />
          ),
        },
      ],
      isSwitch: false,
      showOnPage: "all",
    },
    {
      label: "Discount Type",
      settings: [
        {
          key: "discountSwitch",
          description: "Show only IG Discounts",
          component: (
            <DiscountFilter
              isAllDiscounts={dialogFilters.discountIncludeType}
              setDialogFilters={setDialogFilters}
            />
          ),
        },
      ],
      isSwitch: true,
      showOnPage: "discounts",
      config: shouldShowDiscount?.value,
    },
    {
      label: "URL Parameter",
      settings: [
        {
          key: "urlParam",
          description:
            "Filter to visitors from specific ad experiences or media",
          component: (
            <UrlParamFilter
              onUrlParamChange={onUrlParamChange}
              urlParam={dialogFilters.urlParam}
              onReset={() => {
                setDialogFilters((draft) => {
                  draft.urlParam = {
                    queryParam: null,
                    queryParamValue: null,
                    queryParamFilterType: null,
                  };
                  return draft;
                });
              }}
            />
          ),
        },
      ],
      isSwitch: false,
      showOnPage: "all",
    },
    {
      label: "User Behavior",
      settings: [
        {
          key: "userBehavior",
          description: "Filter by users who:",
          component: (
            <UserBehaviorFilters
              setDialogFilters={setDialogFilters}
              userBehavior={dialogFilters.userBehavior}
            />
          ),
        },
      ],
      isSwitch: false,
      config: resourceType === "experiences",
      showOnPage: "all",
    },
    {
      label: "Z-Score",
      settings: [
        {
          key: "isZScoreEnabled",
          description: "Max Z-Score",
          component: (
            <ZScoreFilter
              isZScoreEnabled={dialogFilters.isZScoreEnabled}
              setDialogFilters={setDialogFilters}
              maxZScore={dialogFilters.maxZScore}
              onBooleanFilterChange={onBooleanFilterChange}
            />
          ),
        },
      ],
      isSwitch: true,
      showOnPage: "all",
    },
    {
      label: "Custom Events",
      settings: [
        {
          key: "customEvents",
          description:
            "Filter to visitors who had at least one of these custom events",
          component: (
            <IgFilterAutocomplete
              selectedValues={dialogFilters.customEvents}
              filterKey={"customEvents"}
              allDataOptions={allCustomEventData!}
              placeholder={"Custom Events"}
              onChange={onStringArrFilterChange}
              disableCloseOnSelect={true}
            />
          ),
        },
      ],
      isSwitch: false,
      showOnPage: "all",
      config:
        currentResource &&
        resourceType === "experiences" &&
        allCustomEventData &&
        allCustomEventData?.length > 0,
    },
    {
      label: "Country Codes",
      settings: [
        {
          key: "countryCodes",
          description: "Filter to visitors who were in one of these countries",
          component: (
            <IgFilterAutocomplete
              selectedValues={dialogFilters.countryCodes}
              onChange={onStringArrFilterChange}
              filterKey={"countryCodes"}
              allDataOptions={allCountryCodesData!}
              placeholder={"Countries"}
              disableCloseOnSelect={true}
              getOptionLabel={(option) => `${CountryEnum[option]} (${option})`}
            />
          ),
        },
      ],
      isSwitch: false,
      showOnPage: "all",
      config:
        currentResource &&
        resourceType === "experiences" &&
        allCountryCodesData &&
        allCountryCodesData?.length > 0,
    },
  ];

  const onFiltersClearAll = () => {
    const initialIsTestOrdersOnly =
      resourceType === "campaigns"
        ? true
        : currentResource.experienceAnalysis.analyticsViewType === "none" ||
          currentResource.experienceAnalysis.analyticsViewType ===
            "test_orders_only";

    dispatch(
      setDashboardFilter({
        ...dashboardFilters,
        testOrdersOnly: initialIsTestOrdersOnly,
        isZScoreEnabled: true,
        startTime: getFirstStartedAtTs(currentResource.experienceActions),
        endTime: getLastEndedAtTs(currentResource.experienceActions),
        deviceType: null,
        discountIncludeType: 1,
        visitorType: null,
        channels: [],
        maxZScore: maxZScoreConfig!.value,
        customEvents: [],
        countryCodes: [],
        userBehavior: { viewedCheckout: null, addedToCart: null },
        urlParam: {
          queryParam: null,
          queryParamFilterType: null,
          queryParamValue: null,
        },
      })
    );

    setDialogFilters((draft) => {
      draft.testOrdersOnly = initialIsTestOrdersOnly;
      draft.discountIncludeType = 1;
      draft.startTime = getLastEndedAtTs(currentResource.experienceActions);
      draft.endTime = getFirstStartedAtTs(currentResource.experienceActions);
      draft.isZScoreEnabled = true;
      draft.maxZScore = maxZScoreConfig!.value;
      draft.deviceType = null;
      draft.channels = [];
      draft.visitorType = null;
      draft.customEvents = [];
      draft.countryCodes = [];
      draft.userBehavior = { viewedCheckout: null, addedToCart: null };
      draft.urlParam = {
        queryParam: null,
        queryParamFilterType: null,
        queryParamValue: null,
      };
      return draft;
    });

    dispatch(setDialog({ dialog: "dashboardFilters", open: false }));
  };

  const onFiltersSave = () => {
    if (isTestProductsDefaultChange) {
      setIsTestProductsDefaultChange(false);
      mutateExperience.mutate({
        id: currentResource.id!,
        analyticsViewType: dialogFilters.testOrdersOnly
          ? "test_orders_only"
          : "sitewide_orders",
      });
    }
    dispatch(setDashboardFilter({ ...dashboardFilters, ...dialogFilters }));
    dispatch(setDialog({ dialog: "dashboardFilters", open: false }));
  };

  return (
    <BaseFiltersDialog
      onFilterSave={onFiltersSave}
      openFilters={openFilters}
      onFiltersClearAll={onFiltersClearAll}
      filterButtons={filterButtons}
      isSaveLoading={mutateExperience.isPaused}
      isSaveDisabled={!isStartValid || !isEndValid}
      pageName={pageName}
    />
  );
};
